import React from 'react';
import { fmtCurrency } from '../../../utils/formatNumber';
import { formatDate }  from '../../../utils/dateUtils';
import { StatusBadge } from '../../../components/common';
import { PLEDGE_STATUS } from '../../../constants/statusMaps';
import styles from '../MemberReports.module.css';

const memberName   = (o) => o.member?.name || o.userId?.name || '—';
const categoryName = (o) => o.DonationCategory?.name || o.donationCategoryId?.name || '—';

export default function PendingBalanceReport({ offerings, viewMode, memberOptions = [], filterFrom = '', filterTo = '' }) {
  const depLabelMap = Object.fromEntries(
    (memberOptions || []).filter((m) => m.badge?.startsWith('Dep.')).map((m) => [String(m.id), m.badge])
  );

  const relevant = offerings
    .filter((o) => Number(o.pendingAmount) > 0 && ['pending', 'partial'].includes(o.status))
    .filter((o) => {
      if (filterFrom && (!o.offeringDate || o.offeringDate < filterFrom)) return false;
      if (filterTo   && (!o.offeringDate || o.offeringDate > filterTo))   return false;
      return true;
    })
    .sort((a, b) => {
      const n = memberName(a).localeCompare(memberName(b));
      return n !== 0 ? n : (Number(b.pendingAmount) || 0) - (Number(a.pendingAmount) || 0);
    });

  const totalPledged = relevant.reduce((s, o) => s + (Number(o.offeredAmount)  || 0), 0);
  const totalPaid    = relevant.reduce((s, o) => s + (Number(o.paidAmount)     || 0), 0);
  const totalPending = relevant.reduce((s, o) => s + (Number(o.pendingAmount)  || 0), 0);

  /* ── Summary ────────────────────────────────────────────────────────────── */
  if (viewMode === 'summary') {
    const grouped = relevant.reduce((acc, o) => {
      const key  = String(o.userId?.id ?? o.userId);
      const name = memberName(o);
      if (!acc[key]) acc[key] = { id: key, name, totalPledged: 0, totalPaid: 0, totalPending: 0 };
      acc[key].totalPledged  += Number(o.offeredAmount)  || 0;
      acc[key].totalPaid     += Number(o.paidAmount)     || 0;
      acc[key].totalPending  += Number(o.pendingAmount)  || 0;
      return acc;
    }, {});
    const rows = Object.values(grouped).sort((a, b) => b.totalPending - a.totalPending);

    return (
      <div className="card">
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th style={{ width: 36 }}>#</th>
                <th>Member</th>
                <th className={styles.amtRight}>Total Offered (₹)</th>
                <th className={styles.amtRight}>Total Paid (₹)</th>
                <th className={styles.amtRight}>Total Pending (₹)</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={5} className="empty-state">No pending balances found.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{rows.indexOf(r) + 1}</td>
                  <td>
                    <strong>{r.name}</strong>
                    {depLabelMap[r.id] && (
                      <span style={{ fontWeight: 400, fontSize: '0.8em', color: 'var(--color-text-muted)', marginLeft: 6 }}>
                        ({depLabelMap[r.id]})
                      </span>
                    )}
                  </td>
                  <td className={styles.amtRight}>{fmtCurrency(r.totalPledged)}</td>
                  <td className={styles.amtRight}>{fmtCurrency(r.totalPaid)}</td>
                  <td className={`${styles.amtRight} ${styles.pending}`}>
                    <strong>{fmtCurrency(r.totalPending)}</strong>
                  </td>
                </tr>
              ))}
            </tbody>
            {rows.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan={2}><strong>Total ({rows.length} member{rows.length !== 1 ? 's' : ''})</strong></td>
                  <td className={styles.amtRight}><strong>{fmtCurrency(totalPledged)}</strong></td>
                  <td className={styles.amtRight}><strong>{fmtCurrency(totalPaid)}</strong></td>
                  <td className={`${styles.amtRight} ${styles.pending}`}>
                    <strong>{fmtCurrency(totalPending)}</strong>
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    );
  }

  /* ── Detail (grouped by member) ────────────────────────────────────────── */
  const groups = relevant.reduce((acc, o) => {
    const key = String(o.userId?.id ?? o.userId);
    if (!acc[key]) acc[key] = { id: key, name: memberName(o), entries: [] };
    acc[key].entries.push(o);
    return acc;
  }, {});
  const memberGroups = Object.values(groups).sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="card">
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th style={{ width: 36 }}>#</th>
              <th>Category</th>
              <th>Date</th>
              <th className={styles.amtRight}>Offered (₹)</th>
              <th className={styles.amtRight}>Paid (₹)</th>
              <th className={styles.amtRight}>Pending (₹)</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {memberGroups.length === 0 && (
              <tr><td colSpan={7} className="empty-state">No pending balances found.</td></tr>
            )}
            {memberGroups.map((group) => {
              const gPledged = group.entries.reduce((s, o) => s + (Number(o.offeredAmount)  || 0), 0);
              const gPaid    = group.entries.reduce((s, o) => s + (Number(o.paidAmount)     || 0), 0);
              const gPending = group.entries.reduce((s, o) => s + (Number(o.pendingAmount)  || 0), 0);
              return (
                <React.Fragment key={group.id}>
                  <tr className={styles.memberGroupRow}>
                    <td colSpan={7}>
                      {group.name}
                      {depLabelMap[group.id] && (
                        <span style={{ fontWeight: 400, fontSize: '0.8em', color: 'var(--color-text-muted)', marginLeft: 6 }}>
                          ({depLabelMap[group.id]})
                        </span>
                      )}
                    </td>
                  </tr>
                  {group.entries.map((o, i) => (
                    <tr key={o.id ?? i}>
                      <td style={{ paddingLeft: 20 }}>{i + 1}</td>
                      <td>{categoryName(o)}</td>
                      <td>{formatDate(o.offeringDate)}</td>
                      <td className={styles.amtRight}>{fmtCurrency(o.offeredAmount)}</td>
                      <td className={styles.amtRight}>{fmtCurrency(o.paidAmount)}</td>
                      <td className={`${styles.amtRight} ${styles.pending}`}>
                        <strong>{fmtCurrency(o.pendingAmount)}</strong>
                      </td>
                      <td><StatusBadge statusMap={PLEDGE_STATUS} status={o.status} /></td>
                    </tr>
                  ))}
                  <tr className={styles.memberSubtotalRow}>
                    <td colSpan={3} style={{ paddingLeft: 20 }}>
                      Subtotal — {group.entries.length} entr{group.entries.length !== 1 ? 'ies' : 'y'}
                    </td>
                    <td className={styles.amtRight}>{fmtCurrency(gPledged)}</td>
                    <td className={styles.amtRight}>{fmtCurrency(gPaid)}</td>
                    <td className={`${styles.amtRight} ${styles.pending}`}>{fmtCurrency(gPending)}</td>
                    <td />
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
          {memberGroups.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={3}><strong>Grand Total ({relevant.length} entr{relevant.length !== 1 ? 'ies' : 'y'}, {memberGroups.length} member{memberGroups.length !== 1 ? 's' : ''})</strong></td>
                <td className={styles.amtRight}><strong>{fmtCurrency(totalPledged)}</strong></td>
                <td className={styles.amtRight}><strong>{fmtCurrency(totalPaid)}</strong></td>
                <td className={`${styles.amtRight} ${styles.pending}`}><strong>{fmtCurrency(totalPending)}</strong></td>
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
